import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "instantsearch.css/themes/satellite.css";
import { history } from "instantsearch.js/es/lib/routers";
import { ComponentType, useEffect, useRef, useState } from "react";
import {
  Configure,
  CurrentRefinements,
  HierarchicalMenu,
  InstantSearch,
  RefinementList,
  SearchBox,
  useInfiniteHits,
  useInstantSearch,
  useRange,
} from "react-instantsearch";
import { useSearch } from "../state";
import { config } from "../state/config";
import { binnenBuitenSort } from "./filterUtil";
import { Header, HitMapping, useMode } from "./Header";

const transformRefinementLabels = (items: any[]) =>
  items.map((item) => ({
    ...item,
    label: config.facetMapping[item.attribute] || item.label,
  }));

// Infinite scroll built on `useInfiniteHits` + IntersectionObserver — replaces
// the prebuilt <InfiniteHits> "Show more" button. Pattern from
// https://www.algolia.com/doc/guides/building-search-ui/ui-and-ux-patterns/infinite-scroll/react
const InfiniteHits = ({
  hitComponent: Hit,
  className = "",
  classNames = {},
}: {
  hitComponent: ComponentType<{ hit: any }>;
  className?: string;
  classNames?: { list?: string; item?: string };
}) => {
  const { items, isLastPage, showMore } = useInfiniteHits();
  const sentinelRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || isLastPage) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) showMore();
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
    // items.length erbij: observe() vuurt altijd een initiële callback, dus
    // als de sentinel na het laden van een pagina nog in beeld staat (pagina
    // vult het scherm niet) laadt de volgende meteen — zonder dat is er geen
    // crossing en blijft de lijst hangen
  }, [isLastPage, showMore, items.length]);

  return (
    <div className={`ais-InfiniteHits ${className}`}>
      <ul className={`ais-InfiniteHits-list ${classNames.list ?? ""}`}>
        {items.map((hit) => (
          <li key={hit.objectID} className={`ais-InfiniteHits-item ${classNames.item ?? ""}`}>
            <Hit hit={hit} />
          </li>
        ))}
        {/* echte hoogte + eigen rij: een leeg (0×0) element op de rand van de
            scroll-container triggert de IntersectionObserver niet betrouwbaar */}
        <li ref={sentinelRef} className="block w-full h-8" aria-hidden="true" />
      </ul>
    </div>
  );
};

export const Block = ({ title, children }: { title: string; children?: React.ReactNode }) => (
  <>
    <h2 className="text-xl font-semibold">{title}</h2>
    {children}
  </>
);

export const Loading = () => {
  const { status } = useInstantSearch();
  if (!["loading", "stalled"].includes(status)) return null;
  return (
    <div className="h-screen bg-white">
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div>
      </div>
    </div>
  );
};

const MapResults = () => {
  const setVisible = useSearch((s) => s.setVisible);

  useEffect(() => {
    setVisible(true);
    return () => setVisible(false);
  }, [setVisible]);

  // In kaartmodus wordt geen enkele hit getoond -- de stippen komen uit een
  // eigen bbox-zoekopdracht (map/searchHits.ts). Ze tóch opvragen is niet
  // alleen verspild werk: Elasticsearch slaat zijn aggregatiecache over zodra
  // een verzoek ook hits vraagt, en juist het facetverzoek zonder filter duurt
  // ruim acht seconden. Met nul hits is hetzelfde antwoord na de eerste keer
  // in enkele milliseconden terug.
  return <Configure hitsPerPage={0} />;
};

const FacetContent = ({
  facetClass = "sm:w-[20rem] lg:w-[23rem]",
  className = "pl-10 sm:pl-20",
  children,
}: FacetProps) => {
  const { mode = "map" } = useMode();
  // @ts-ignore
  const [Hit, hitStyle] = HitMapping[mode] || HitMapping["grid"];
  const map = mode === "map";

  return (
    <div
      className={`${className} fixed mt-14 flex z-10 h-screen bg-white${map ? "" : " w-screen"}`}
    >
      <div className={`${facetClass} rounded-t-2xl flex-none overflow-y-scroll p-2`}>
        <Header />
        {children}
        <div className="py-7" />
      </div>
      <div className="flex-grow z-9 bg-sidebar-menu overflow-y-scroll w-0">
        <CurrentRefinements
          className="p-2 fixed bg-sidebar-menu [&.ais-CurrentRefinements--noRefinement]:p-0"
          transformItems={transformRefinementLabels}
        />
        <Loading />
        {map ? (
          <MapResults />
        ) : (
          <InfiniteHits
            hitComponent={Hit}
            className="pt-10 pb-10"
            classNames={{ item: hitStyle }}
          />
        )}
      </div>
    </div>
  );
};

// https://www.algolia.com/doc/api-reference/widgets/instantsearch/js/#widget-param-future
const future = { preserveSharedStateOnUnmount: true };
const hist = history({ cleanUrlOnDispose: true });

interface FacetProps {
  facetClass?: string;
  className?: string;
  children: React.ReactNode;
}

export const BaseFacet = ({ children, ...props }: FacetProps) => {
  const searchClient = useSearch((s) => s.client);

  return (
    <InstantSearch
      indexName="reference"
      searchClient={searchClient}
      future={future}
      routing={{ router: hist }}
    >
      <FacetContent {...props}>{children}</FacetContent>
    </InstantSearch>
  );
};

// A number or "" for an unset bound (useRange uses ±Infinity for unset)
const boundToInput = (v: number | undefined) => (v === undefined || !isFinite(v) ? "" : String(v));
const inputToBound = (v: string): number | undefined =>
  v === "" || isNaN(Number(v)) ? undefined : Number(v);

// RangeInput without the "Ga" submit button: refines automatically, debounced
// until 400ms after the last change. Reuses the ais-* classes for theming.
const DebouncedRangeInput = ({ attribute, delay = 400 }: { attribute: string; delay?: number }) => {
  const { range, start, refine } = useRange({ attribute });
  const [min, setMin] = useState("");
  const [max, setMax] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Sync inputs when the refinement changes elsewhere (URL routing,
  // CurrentRefinements-clear). Value deps, since `start` is a fresh tuple
  // per render.
  useEffect(() => {
    setMin(boundToInput(start[0]));
    setMax(boundToInput(start[1]));
  }, [start[0], start[1]]);

  const update = (nextMin: string, nextMax: string) => {
    setMin(nextMin);
    setMax(nextMax);
    clearTimeout(timer.current);
    const [lo, hi] = [inputToBound(nextMin), inputToBound(nextMax)];
    // Incomplete/inverted range (min > max): don't fire, wait for a fix
    if (lo !== undefined && hi !== undefined && lo > hi) return;
    timer.current = setTimeout(() => refine([lo, hi]), delay);
  };
  useEffect(() => () => clearTimeout(timer.current), []);

  return (
    <div className="ais-RangeInput">
      <form className="ais-RangeInput-form" onSubmit={(e) => e.preventDefault()}>
        <input
          type="number"
          className="ais-RangeInput-input ais-RangeInput-input--min"
          placeholder={boundToInput(range.min)}
          min={range.min}
          max={range.max}
          value={min}
          onChange={(e) => update(e.target.value, max)}
        />
        <span className="ais-RangeInput-separator">-</span>
        <input
          type="number"
          className="ais-RangeInput-input ais-RangeInput-input--max"
          placeholder={boundToInput(range.max)}
          min={range.min}
          max={range.max}
          value={max}
          onChange={(e) => update(min, e.target.value)}
        />
      </form>
    </div>
  );
};

const bClass = "p-2";

export const Filter = ({ label, children }: { label?: string; children: React.ReactNode }) => (
  <div className={bClass}>
    <p>{label || config.facetMapping["attribute"]}</p>
    {children}
  </div>
);

const TogglableFilter = ({ label, children }: { label?: string; children: React.ReactNode }) => {
  // reference[refinementList][city][0]
  const [open, setOpen] = useState(decodeURIComponent(document.location.search).includes("[city]"));
  if (!open)
    return (
      <button
        onClick={() => setOpen(true)}
        className="inline-flex border ml-2 px-2 py-1 text-sm font-medium  transition-colors bg-gray-100 text-gray-500 hover:bg-gray-200 rounded-md mr-3"
      >
        Zoek op {label || config.facetMapping["attribute"]}
      </button>
    );
  return (
    <div className={bClass}>
      <p>
        {label}
        <button onClick={() => setOpen(false)}>
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </p>
      {children}
    </div>
  );
};

// Ook gebruikt door de workspace, daar verborgen: setIndexUiState werkt alleen
// op gemounte widgets, dus de filters moeten in de DOM staan om een voorgesteld
// filter te kunnen toepassen.
export const Filters = () => (
  <>
    <div className="py-2">
      <SearchBox placeholder="Zoek op adres / kadastraal nummer" />
    </div>
    <Block title="Basisgegevens">
      <Filter label="Provincie/gemeente">
        <HierarchicalMenu attributes={["zone_1", "zone_2"]} limit={50} sortBy={["name"]} />
      </Filter>
      <TogglableFilter label="Woonplaats">
        <RefinementList attribute="city" searchable={true} limit={10} />
      </TogglableFilter>
      {/* Expliciete lijst: in welk blok een filter hoort is een UI-keuze, niet
          iets dat uit config.facets af te leiden valt. city_inhabitants is ook
          een bereik, maar staat hieronder bij Ontwikkeling. */}
      {["area", "building_area", "building_year"].map((attr) => (
        <Filter key={attr} label={config.facetMapping[attr]}>
          <DebouncedRangeInput attribute={attr} />
        </Filter>
      ))}
      <Filter label="Grondgebruik">
        <RefinementList attribute="land_cover" />
      </Filter>
      <Filter label="Enkelbestemming">
        <RefinementList attribute="zoning_single" limit={50} />
      </Filter>
      <Filter label="Bestemming (POI)">
        <RefinementList attribute="destination" />
      </Filter>
      <Filter label="Gebruiksdoel">
        <RefinementList attribute="usage_goal" limit={15} />
      </Filter>
    </Block>
    <Block title="Milieu, ecologie en bodem">
      {["soil", "arch_monuments", "nnn", "natura_2000"].map((attr) => (
        <Filter key={attr} label={config.facetMapping[attr]}>
          <RefinementList attribute={attr} />
        </Filter>
      ))}
    </Block>
    <Block title="Ontwikkeling">
      <Filter label={config.facetMapping["city_inhabitants"]}>
        <DebouncedRangeInput attribute={"city_inhabitants"} />
      </Filter>
      {["built_up_area", "urban_area"].map((attr, i) => (
        <Filter key={attr} label={config.facetMapping[attr]}>
          <RefinementList attribute={attr} sortBy={binnenBuitenSort} />
        </Filter>
      ))}
      <RefinementList attribute="novex" />
      <RefinementList attribute="industry" />
    </Block>
    {/*<Block title="Markt en Ontwikkelpotentie"></Block>*/}
  </>
);

export const Facet = () => (
  <BaseFacet>
    <Filters />
  </BaseFacet>
);
